import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { useCart } from '@/hooks/useCart';
import { productService } from '@/services/product.service';
import { Product } from '@/types/product.types';
import { formatPrice } from '@/utils/formatters';
import { cartLineKey, getCartLineDisplayName, getCartLineUnitPrice } from '@/utils/cartLine';
import {
  ShoppingCartIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

export const Panier = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, cartTotal } = useCart();
  const [products, setProducts] = useState<Map<number, Product>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadProducts = async () => {
      if (cart.length === 0) {
        setProducts(new Map());
        return;
      }

      setLoading(true);
      try {
        const productMap = new Map<number, Product>();
        for (const item of cart) {
          try {
            const product = await productService.getById(item.id);
            if (product) {
              productMap.set(item.id, product);
            }
          } catch {
            // Produit indisponible — ignoré
          }
        }
        setProducts(productMap);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [cart]);

  const handleCheckout = () => {
    if (cart.length === 0) return;
    navigate('/checkout');
  };

  return (
    <>
      <Navbar />
      <main className="client-page">
        <div className="client-page-container">
          <h1 className="gtitle client-page-title">
            <span className="gtitle-light">{t('clientPages.cart.titleLight')}</span>{' '}
            <span className="gtitle-strong">{t('clientPages.cart.titleStrong')}</span>
          </h1>

          {loading && cart.length > 0 && (
            <p className="client-page-muted">{t('clientPages.cart.loading')}</p>
          )}

          {!loading && cart.length === 0 && (
            <div className="client-page-empty">
              <ShoppingCartIcon className="client-page-empty-icon" aria-hidden="true" />
              <p>{t('clientPages.cart.empty')}</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/boutique')}>
                {t('clientPages.cart.shopCta')}
              </button>
            </div>
          )}

          {cart.length > 0 && (
            <>
              <ul className="client-cart-list">
                {cart.map((item) => {
                  const product = products.get(item.id);
                  const unitPrice = getCartLineUnitPrice(item, product);
                  const lineTotal = unitPrice * item.quantity;

                  return (
                    <li key={cartLineKey(item)} className="client-cart-item">
                      <div className="client-cart-item-info">
                        <p className="client-cart-item-name">{getCartLineDisplayName(item, product)}</p>
                        <p className="client-page-muted">{formatPrice(unitPrice)}</p>
                      </div>
                      <div className="client-cart-item-actions">
                        <button
                          type="button"
                          className="client-cart-qty-btn"
                          onClick={() => updateQuantity(item.id, item.quantity - 1, item.selected_pack_id)}
                          aria-label={t('clientPages.cart.decrease')}
                        >
                          <MinusIcon />
                        </button>
                        <span className="client-cart-qty">{item.quantity}</span>
                        <button
                          type="button"
                          className="client-cart-qty-btn"
                          onClick={() => updateQuantity(item.id, item.quantity + 1, item.selected_pack_id)}
                          aria-label={t('clientPages.cart.increase')}
                        >
                          <PlusIcon />
                        </button>
                        <button
                          type="button"
                          className="client-cart-remove"
                          onClick={() => removeFromCart(item.id, item.selected_pack_id)}
                          aria-label={t('clientPages.cart.remove')}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                      <p className="client-cart-line-total">{formatPrice(lineTotal)}</p>
                    </li>
                  );
                })}
              </ul>

              <div className="client-cart-summary">
                <div className="client-cart-summary-row">
                  <span>{t('clientPages.cart.total')}</span>
                  <strong>{formatPrice(cartTotal)}</strong>
                </div>
                <button type="button" className="btn btn-primary client-cart-checkout" onClick={handleCheckout}>
                  {t('clientPages.cart.checkout')}
                  <ArrowRightIcon className="client-cart-checkout-icon" aria-hidden="true" />
                </button>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};
